<template>
    <main id="main">

        <section class="story__body" data-flex="row" v-if="story.theme !== 'staggered'">

            <div v-if="story.theme !== 'slider'">
                <div class="circle image-ratio" v-if="story.images.some(el => el.type !== 'image')">
                    <circle-video
                        :media="story.images[0].media"
                        :custom="true"
                    />
                </div>
                <story-image
                    v-else
                    v-for="(circle, c) in story.images"
                    className="circle circle--image"
                    :key="`circ-${c}-${tempId}`"
                    :src="mediaPath(story, circle.media)"
                    :priority="c < 1 ? 'high' : ''"
                />
            </div>
            <div v-else>
                <Slider 
                    :slug="slug" 
                    :images="story.images" 
                    :id="storyId"
                    :moduleType="moduleType"
                />
            </div>

            <div v-if="story.body">

                <StoryHeader
                    :title="story.title"
                    :author="story.author"
                    :category="isMainPath && category"
                />

                <template v-for="(body, i) in story.body">
                    <p 
                        :key="`story-body-${i}`" 
                        v-html="body"
                        v-if="typeof body === 'string'" 
                    />
                    <component
                        :is="`RecruitmentButtons`"
                        :key="`story-buttons-${i}`" 
                        v-else-if="body instanceof Object && body.type.startsWith('buttons')"
                    />
                    <ul
                        :key="`story-list-${i}`" 
                        v-else-if="body instanceof Object && body.type.startsWith('list')"
                    >
                        <li 
                            v-for="(item, x) in body.items"
                            v-text="item"
                            :key="`li-${i}-${x}`"
                        />
                    </ul>
                    <p :key="`story-btn-${i}`" v-else-if="body instanceof Object && body.type.startsWith('router-lin')">
                        {{ body.title }}
                        <router-link
                            :key="`story-link-${i}`" 
                            :to="body.path"
                            :target="body.target"
                            class="inner-link"
                            v-html="`<span>${body.text}</span>`"
                        />
                    </p>
                </template>
            </div>

        </section>
        
        <template v-else>
            <section class="story__body" data-flex="row">
                <div>
                    <story-image
                        className="circle circle--image"
                        :key="`circle-${tempId}`"
                        :src="mediaPath(story, story.images[0].media)"
                        priority="high"
                    />
                </div>
                <div v-if="story.body">
                    <StoryHeader
                        :title="story.title"
                        :author="story.author"
                        :category="isMainPath && category"
                    />
                    
                    <p v-html="story.body[0]" />
                </div>

            </section>
            <section class="story__body" data-flex="row" v-if="story.theme == 'staggered'">

                <div v-if="story.images.length > 1">
                    <story-image
                        className="circle circle--image"
                        :key="`circle-${tempId}`"
                        :src="mediaPath(story, story.images[1].media)"
                    />
                </div>
                <div>
                    <p v-html="story.body[1]" />
                </div>

            </section>
        </template>

        <nav v-if="isMainPath" :aria-labelledby="navTitleId" class="story__pages pages__nav">
            <header data-flex data-flex--justify>
                <h2 :id="navTitleId" class="subtitle"><i role="presentation" />
                    {{ navTitle }}
                </h2>
            </header>
            <ul data-flex="row">
                <li v-for="item in sortMembers(group)" :key="item.title">
                    <router-link :to="href(item)" class="nav-link" active-class="is-selected">
                        <div class="image-ratio">
                            <img width="60" height="60" :src="mediaPath(story, item.images[0].media)" alt />
                        </div>
                        <span class="nav-link__text" v-html="item.author.name.split(' ').join('<br>')" />
                    </router-link>
                </li>
            </ul>
        </nav>

        <!-- <SplashImage
            v-show="hasStudentPath || moduleType == 'recruit'"
        /> -->

    </main>

</template>

<script>
import { mapState } from 'vuex'
import { adjustSlugFromTitle, adjustTitleFromSlug, adjustStorySlugByPath } from '@/mixins/lib/textFormat'

import StoryHeader          from '../Story/StoryHeader'
import StoryImage           from '../Story/StoryImage'
import Socials              from '../Ui/Socials'

export default {
    name: 'StoryItem',

    props: {
        slug: {
            type: String,
            default: 'dir'
        },
        story: {
            type: Object,
            default: null
        },
        group: {
            type: Array,
        },
    },

    components: {
        StoryHeader,
        StoryImage,
        Socials,
        Slider: () => import(/* webpackChunkName: "ui-slider" */ '@/components/Ui/Slider'),
        CircleVideo: () => import(/* webpackChunkName: "ui-circle-video" */ '@/components/Ui/CircleVideo'),
        RecruitmentButtons: () => import(/* webpackChunkName: "ui-cta-buttons" */ '@/components/Recruit/RecruitmentButtons'),
    },

    data: () => ({
        loading: false,
    }),

    inject: ['moduleType'],

    methods: {
        mediaPath( story, media, ext = '.png' ) {
            if (String(this.slug).length > 1 ) {
                return `/static/stories/${this.slug}/story/${media}${ext}`
            }
            return `/static/stories/story/${media}${ext}`
        },

        sortMembers( members ) {
            if (!members) return members
            
            return members.sort((x, d) => {
                const slugA = x.slug.toLowerCase()
                const slugB = d.slug.toLowerCase()

                return slugA < slugB ? -1 : 1
            })
        },

        href( story ) {
            const { path } = this.$route
            const adjustedSlug = adjustStorySlugByPath(story.slug, path)

            if ( adjustedSlug.includes('create-your-own')) {
                return adjustedSlug
            }

            return `${adjustedSlug}-${adjustSlugFromTitle(story.author.name).toLowerCase().replace('_', '-')}`
        },
    },

    computed: {
        isMainPath() {
            return this.$route.fullPath.includes('/discover-your')
        },
        category() {
            if (this.story.slug) {
                return adjustTitleFromSlug(this.story.slug)
            }
            return ''
        },
        navTitle() {
            if (this.category) {
                return `Read more ${this.category} stories`
            }
            return 'Read more'
        },
        navTitleId() {
            return `story-title-${this.tempId}`
        },
        storyId() {
            return this.tempId
        },
        tempId() {
            return Math.random(10) * 3
        },
        ...mapState({
            hasStudentPath(state, getters) {
                return getters['isUserStudent']
            },
        }),
    }
}
</script>
